import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdmin } from './AdminContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Users, Building2, Truck, ClipboardList,
  BarChart3, LogOut, Menu, X, ChevronDown, Shield
} from 'lucide-react';

const NAV = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'operations'], end: true },
  { to: '/admin/clients', icon: Building2, label: 'Clients', roles: ['admin', 'operations', 'accounting'] },
  { to: '/admin/vendors', icon: Truck, label: 'Vendors', roles: ['admin', 'operations', 'accounting'] },
  { to: '/admin/users', icon: Users, label: 'User Management', roles: ['admin'] },
  { to: '/admin/activity-log', icon: ClipboardList, label: 'Activity Log', roles: ['admin'] },
  { to: '/admin/reports', icon: BarChart3, label: 'Financial Reports', roles: ['admin', 'accounting'] },
];

export default function AdminLayout() {
  const { user, logout, hasRole } = useAdmin();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const filteredNav = NAV.filter(n => n.roles.some(r => hasRole(r)));

  const roleBadge = { admin: 'Admin', operations: 'Operations', accounting: 'Accounting' };
  const roleColor = { admin: '#B8962E', operations: '#3B82F6', accounting: '#10B981' };

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#B8962E' }}>
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>2TI CORP</div>
            <div className="text-xs" style={{ color: '#B8962E' }}>Admin Portal</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`
            }
            style={({ isActive }) => isActive ? { background: 'rgba(184,150,46,0.2)', color: '#B8962E' } : {}}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: roleColor[user?.role] || '#666' }}>
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs px-1.5 py-0.5 rounded-full inline-block" style={{ background: `${roleColor[user?.role]}22`, color: roleColor[user?.role] }}>
              {roleBadge[user?.role]}
            </p>
          </div>
        </div>
        <Button onClick={handleLogout} variant="ghost" className="w-full justify-start gap-2 text-white/60 hover:text-white hover:bg-white/10" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 z-30" style={{ background: '#1B2A6B' }}>
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col transform transition-transform duration-200 lg:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ background: '#1B2A6B' }}>
        <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white/60 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 sm:px-6 h-14 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: roleColor[user?.role] || '#666' }}>
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
